import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Clapperboard,
  Download,
  Loader2,
  Trash2,
} from "lucide-react";
import type { VideoProjectDTO } from "@app/shared";
import { api, videoUrl } from "../lib/api";
import { useActiveBrand } from "../lib/brand";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { useToast } from "../components/Toast";

type VideoFormat = "vertical" | "square" | "landscape";
const FORMATS: { value: VideoFormat; label: string; aspect: string }[] = [
  { value: "vertical", label: "Vertikal 9:16 (Reels/Shorts)", aspect: "9 / 16" },
  { value: "square", label: "Persegi 1:1", aspect: "1 / 1" },
  { value: "landscape", label: "Lanskap 16:9", aspect: "16 / 9" },
];
const ASPECT: Record<string, string> = {
  vertical: "9 / 16",
  square: "1 / 1",
  landscape: "16 / 9",
};

/**
 * Video (prototype): the operator gives a topic; AI writes a narrated script,
 * then we assemble a short educational video from licensed Pexels stock clips
 * (creator credited) + Indonesian TTS voice-over + branded on-screen text. No
 * scheduling — manual generate + download only.
 */
export function Video() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const accountKey = useActiveBrand();
  const [format, setFormat] = useState<VideoFormat>("vertical");
  const [topic, setTopic] = useState("");

  const videosQuery = useQuery({
    queryKey: ["videos", accountKey],
    queryFn: ({ signal }) => api.videos({ accountKey: accountKey! }, signal),
    enabled: !!accountKey,
    refetchInterval: (query) =>
      (query.state.data ?? []).some((v) => v.status === "generating")
        ? 5000
        : false,
  });

  const generate = useMutation({
    mutationFn: () => {
      if (!accountKey) throw new Error("Pilih brand terlebih dahulu");
      return api.generateVideo({ accountKey, topic: topic.trim(), format });
    },
    onSuccess: () => {
      toast.success(
        "Video sedang dibuat… (naskah → klip → voice-over → render; ±1–2 menit)",
      );
      void queryClient.invalidateQueries({ queryKey: ["videos"] });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Gagal membuat video"),
  });

  const videos = videosQuery.data ?? [];
  const canGenerate =
    !!accountKey && topic.trim().length > 0 && !generate.isPending;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-navy">
            <Clapperboard className="h-6 w-6 text-teal" />
            Video edukasi{" "}
            <Badge variant="secondary" className="ml-1 align-middle">
              prototype
            </Badge>
          </h1>
          <p className="text-sm text-slate-500">
            AI menulis naskah → klip stok Pexels (kreator dicantumkan) +
            voice-over Indonesia + teks ber-brand → MP4. Manual, tanpa jadwal.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Buat video</CardTitle>
          <CardDescription>
            Tulis topik edukasi. AI pecah jadi 3–4 segmen bernarasi. Fakta yang
            Anda tulis dipakai apa adanya.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="vid-format">Format</Label>
            <select
              id="vid-format"
              value={format}
              onChange={(e) => setFormat(e.target.value as VideoFormat)}
              disabled={generate.isPending}
              className="flex h-10 w-full max-w-md rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-1"
            >
              {FORMATS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="vid-topic">Topik</Label>
            <Textarea
              id="vid-topic"
              rows={4}
              placeholder="Contoh: Jelaskan apa itu produk/layanan Anda dan 3 manfaat utamanya bagi pelanggan."
              value={topic}
              disabled={generate.isPending}
              onChange={(e) => setTopic(e.target.value)}
            />
          </div>
          <div>
            <Button
              variant="primary"
              onClick={() => generate.mutate()}
              disabled={!canGenerate}
            >
              {generate.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Clapperboard className="h-4 w-4" />
              )}
              Generate
            </Button>
          </div>
        </CardContent>
      </Card>

      <h2 className="text-sm font-medium text-slate-500">
        Hasil <span className="capitalize">({accountKey})</span>
      </h2>

      {videosQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-teal" />
        </div>
      ) : videos.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center text-sm text-slate-500">
            Belum ada video. Buat video pertama Anda di atas.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {videos.map((v) => (
            <VideoCard key={v.id} video={v} />
          ))}
        </div>
      )}
    </div>
  );
}

function VideoCard({ video }: { video: VideoProjectDTO }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const aspect = ASPECT[video.format] ?? "9 / 16";

  const remove = useMutation({
    mutationFn: () => api.deleteVideo(video.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["videos"] });
      toast.success("Video dihapus");
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Gagal menghapus"),
  });

  return (
    <Card>
      <div
        className="relative flex items-center justify-center overflow-hidden rounded-t-xl bg-slate-900"
        style={{ aspectRatio: aspect }}
      >
        {video.status === "ready" && video.hasVideo ? (
          <video
            src={videoUrl(video)}
            controls
            playsInline
            preload="metadata"
            className="h-full w-full object-contain"
          />
        ) : video.status === "failed" ? (
          <div className="flex flex-col items-center gap-1 p-4 text-center text-coral">
            <AlertTriangle className="h-6 w-6" />
            <span className="text-xs">Gagal dibuat</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 p-4 text-center text-slate-300">
            <Loader2 className="h-6 w-6 animate-spin text-teal" />
            <span className="text-xs">Sedang dibuat… (±1–2 mnt)</span>
          </div>
        )}
      </div>
      <CardContent className="space-y-3 p-4">
        <div>
          <p className="line-clamp-1 font-medium text-slate-900">
            {video.title ?? "(tanpa judul)"}
          </p>
          <p className="line-clamp-2 text-xs text-slate-500">{video.topic}</p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-slate-400">
          {video.durationSec ? (
            <Badge variant="secondary">{Math.round(video.durationSec)} dtk</Badge>
          ) : null}
          {video.segments.length > 0 ? (
            <Badge variant="secondary">{video.segments.length} segmen</Badge>
          ) : null}
          <span className="capitalize">{video.format}</span>
        </div>
        {video.credits.length > 0 ? (
          <p className="line-clamp-2 text-[11px] text-slate-400">
            Courtesy: {video.credits.map((c) => c.author).join(", ")} · Pexels
          </p>
        ) : null}
        {video.status === "failed" && video.errorMsg ? (
          <p className="line-clamp-2 text-xs text-coral">{video.errorMsg}</p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          {video.status === "ready" && video.hasVideo ? (
            <a
              href={videoUrl(video)}
              download={`${video.format}-${video.id}.mp4`}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              <Download className="h-4 w-4" />
              MP4
            </a>
          ) : null}
          <Button
            size="sm"
            variant="outline"
            className="text-coral hover:text-coral"
            onClick={() => {
              if (window.confirm("Hapus video ini?")) remove.mutate();
            }}
            disabled={remove.isPending}
          >
            {remove.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            Hapus
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
